import { Head, router } from "@inertiajs/react"
import { Download, BarChart2 } from "lucide-react"
import { useState, useCallback, type ReactNode } from "react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

import { AdminLayout } from "@/layouts/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import type {
  DatePoint,
  ChannelBreakdown,
  ContentSourceBreakdown,
  PoiMetric,
  CategoryCount,
  PeakHoursEntry,
  AnalyticsDateRange,
  PropertySummaryAnalytics,
} from "@/lib/types"

interface Props {
  property: PropertySummaryAnalytics
  sessions_over_time: DatePoint[]
  activation_channels: ChannelBreakdown[]
  top_pois_by_views: PoiMetric[]
  top_pois_by_directions: PoiMetric[]
  top_pois_by_bookings: PoiMetric[]
  category_popularity: CategoryCount[]
  peak_hours: PeakHoursEntry[]
  inside_views: CategoryCount[]
  content_source_breakdown: ContentSourceBreakdown[]
  date_range: AnalyticsDateRange
  export_url: string
}

const PRESET_DAYS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
] as const

const CHANNEL_COLORS: Record<string, string> = {
  qr_scan:       "#3b82f6",
  wifi_redirect: "#10b981",
  direct_link:   "#f59e0b",
  pdf_letter:    "#8b5cf6",
  unknown:       "#6b7280",
}

const CHART_COLOR = "#3b82f6"

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const HOUR_BUCKETS = [0, 3, 6, 9, 12, 15, 18, 21]

// Pre-defined intensity classes so Tailwind can detect them at build time.
// Using a lookup avoids dynamic class generation (e.g. `bg-blue-500/${n}`)
// which Tailwind cannot reliably detect and will strip from the output CSS.
const HEATMAP_INTENSITY_CLASSES = [
  "bg-blue-200",
  "bg-blue-300",
  "bg-blue-300",
  "bg-blue-400",
  "bg-blue-400",
  "bg-blue-500",
  "bg-blue-500",
  "bg-blue-600",
  "bg-blue-700",
  "bg-blue-800",
]

function SectionHeader({ title }: { title: string }) {
  return (
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
    </CardHeader>
  )
}

function EmptyChart() {
  return (
    <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
      No data for this period
    </div>
  )
}

function HorizontalBarChart({
  data,
  nameKey,
}: {
  data: Array<PoiMetric | CategoryCount>
  nameKey: string
}) {
  if (data.length === 0) return <EmptyChart />
  // 36px per row gives readable bars; minimum 120px for very short lists.
  const chartHeight = Math.max(data.length * 40, 120)
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart layout="vertical" data={data} margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} />
        {/* width=180 accommodates most POI names; text is truncated by the SVG clip region */}
        <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 11 }} width={180} />
        <Tooltip />
        <Bar dataKey="count" fill={CHART_COLOR} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function PeakHoursTable({ data }: { data: PeakHoursEntry[] }) {
  if (data.length === 0) return <EmptyChart />

  const lookup = new Map<string, number>()
  let maxCount = 0
  for (const entry of data) {
    lookup.set(`${entry.dow}-${entry.hour}`, entry.count)
    if (entry.count > maxCount) maxCount = entry.count
  }

  function cellIntensityClass(count: number): string {
    if (count === 0) return ""
    const index = Math.min(Math.round((count / maxCount) * 9), 9)
    return HEATMAP_INTENSITY_CLASSES[index]
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" role="grid" aria-label="Guest activity by day and hour">
        <thead>
          <tr>
            <th scope="col" className="pr-2 text-left font-normal text-muted-foreground">
              <span className="sr-only">Day</span>
            </th>
            {HOUR_BUCKETS.map((h) => (
              <th
                key={h}
                scope="col"
                className="px-1 py-1 font-normal text-muted-foreground"
              >
                {h.toString().padStart(2, "0")}:00
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DOW_LABELS.map((day, dow) => (
            <tr key={dow}>
              <th scope="row" className="pr-2 text-left font-normal text-muted-foreground">
                {day}
              </th>
              {HOUR_BUCKETS.map((bucketStart) => {
                const count = Array.from({ length: 3 }, (_, i) =>
                  lookup.get(`${dow}-${bucketStart + i}`) ?? 0,
                ).reduce((a, b) => a + b, 0)
                return (
                  <td
                    key={bucketStart}
                    className={`rounded px-1 py-1.5 text-center tabular-nums ${cellIntensityClass(count)}`}
                    title={`${day} ${bucketStart.toString().padStart(2, "0")}:00–${(bucketStart + 3).toString().padStart(2, "0")}:00: ${count} events`}
                  >
                    {count > 0 ? count : ""}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function AnalyticsIndex({
  property,
  sessions_over_time,
  activation_channels,
  top_pois_by_views,
  top_pois_by_directions,
  top_pois_by_bookings,
  category_popularity,
  peak_hours,
  inside_views,
  content_source_breakdown,
  date_range,
  export_url,
}: Props) {
  // Sync custom inputs to the active server-confirmed date range so that
  // after a preset click the Custom fields reflect the current range.
  const [customFrom, setCustomFrom] = useState(date_range.from)
  const [customTo, setCustomTo] = useState(date_range.to)
  const [showCustom, setShowCustom] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const [customError, setCustomError] = useState<string | null>(null)

  // Determine which preset (if any) matches the active date range so we can
  // highlight it. Compare day counts rather than exact strings because the
  // server may normalise the "to" date to today's date.
  const activeDays = (() => {
    const from = new Date(date_range.from)
    const to = new Date(date_range.to)
    const diff = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    return PRESET_DAYS.find(({ days }) => days === diff)?.days ?? null
  })()

  const navigate = useCallback(
    (from: string, to: string) => {
      setNavigating(true)
      router.get(
        window.location.pathname,
        { from, to },
        {
          preserveState: false,
          onFinish: () => setNavigating(false),
        },
      )
    },
    [],
  )

  function applyPreset(days: number) {
    setShowCustom(false)
    setCustomError(null)
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - days)
    const fromStr = from.toISOString().slice(0, 10)
    const toStr = to.toISOString().slice(0, 10)
    // Keep custom fields in sync so they reflect the active range if
    // the user subsequently opens the Custom panel.
    setCustomFrom(fromStr)
    setCustomTo(toStr)
    navigate(fromStr, toStr)
  }

  function applyCustom() {
    setCustomError(null)
    if (!customFrom || !customTo) {
      setCustomError("Please enter both a start and end date.")
      return
    }
    if (customFrom > customTo) {
      setCustomError("Start date must be on or before the end date.")
      return
    }
    navigate(customFrom, customTo)
  }

  const hasAnyData =
    sessions_over_time.length > 0 ||
    activation_channels.length > 0 ||
    category_popularity.length > 0 ||
    top_pois_by_views.length > 0 ||
    top_pois_by_directions.length > 0 ||
    top_pois_by_bookings.length > 0 ||
    inside_views.length > 0 ||
    peak_hours.length > 0 ||
    content_source_breakdown.length > 0

  return (
    <>
      <Head title={`Analytics — ${property.name}`} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{property.name}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">Guest engagement analytics</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={export_url} download>
              <Download className="mr-1.5 size-3.5" aria-hidden="true" />
              Export CSV
            </a>
          </Button>
        </div>

        {/* Date range controls */}
        <div className="flex flex-wrap items-start gap-2">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Date range presets">
            {PRESET_DAYS.map(({ label, days }) => (
              <Button
                key={label}
                variant={activeDays === days ? "secondary" : "outline"}
                size="sm"
                onClick={() => applyPreset(days)}
                disabled={navigating}
                aria-pressed={activeDays === days}
              >
                {label}
              </Button>
            ))}
          </div>

          <Button
            variant={showCustom ? "secondary" : "outline"}
            size="sm"
            onClick={() => {
              setShowCustom((v) => !v)
              setCustomError(null)
            }}
            aria-expanded={showCustom}
          >
            Custom
          </Button>

          {showCustom && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="analytics-from" className="text-xs text-muted-foreground">
                  From
                </Label>
                <Input
                  id="analytics-from"
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomFrom(e.target.value)
                    setCustomError(null)
                  }}
                  className="h-8 w-36 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="analytics-to" className="text-xs text-muted-foreground">
                  To
                </Label>
                <Input
                  id="analytics-to"
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomTo(e.target.value)
                    setCustomError(null)
                  }}
                  className="h-8 w-36 text-sm"
                />
              </div>
              <Button size="sm" onClick={applyCustom} disabled={navigating || !customFrom || !customTo}>
                Apply
              </Button>
              {customError && (
                <p role="alert" className="w-full text-xs text-destructive">
                  {customError}
                </p>
              )}
            </div>
          )}

          {navigating && (
            <span className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
              Loading…
            </span>
          )}
        </div>

        {/* Empty state */}
        {!hasAnyData && !navigating && (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <BarChart2 className="size-8 text-muted-foreground/40" aria-hidden="true" />
              <p className="font-medium">No activity yet for this period</p>
              <p className="text-sm text-muted-foreground">
                Try a wider date range or wait for guests to start using their guide.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Chart grid */}
        {(hasAnyData || navigating) && (
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Sessions over time */}
            <Card className="lg:col-span-2">
              <SectionHeader title="Guest sessions over time" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : sessions_over_time.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={192}>
                    <LineChart data={sessions_over_time} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={CHART_COLOR}
                        strokeWidth={2}
                        dot={false}
                        name="Sessions"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Activation channels */}
            <Card>
              <SectionHeader title="Activation channels" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : activation_channels.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={192}>
                    <PieChart>
                      <Pie
                        data={activation_channels}
                        dataKey="count"
                        nameKey="channel"
                        cx="50%"
                        cy="50%"
                        outerRadius={72}
                        // Labels on pie slices cause heavy overlap in small containers.
                        // The Legend below already identifies each slice by name + colour.
                        label={false}
                      >
                        {activation_channels.map((entry) => (
                          <Cell key={entry.channel} fill={CHANNEL_COLORS[entry.channel] ?? "#6b7280"} />
                        ))}
                      </Pie>
                      <Legend
                        formatter={(value: string) => value.replace(/_/g, " ")}
                        wrapperStyle={{ fontSize: 11 }}
                      />
                      <Tooltip formatter={(value) => [value, "Sessions"]} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Category popularity */}
            <Card>
              <SectionHeader title="Category popularity" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={category_popularity} nameKey="category" />
                )}
              </CardContent>
            </Card>

            {/* Top POIs by views */}
            <Card>
              <SectionHeader title="Top POIs — views" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={top_pois_by_views} nameKey="name" />
                )}
              </CardContent>
            </Card>

            {/* Top POIs by directions */}
            <Card>
              <SectionHeader title="Top POIs — directions" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={top_pois_by_directions} nameKey="name" />
                )}
              </CardContent>
            </Card>

            {/* Top POIs by bookings */}
            <Card>
              <SectionHeader title="Top POIs — bookings" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={top_pois_by_bookings} nameKey="name" />
                )}
              </CardContent>
            </Card>

            {/* INSIDE info views */}
            <Card>
              <SectionHeader title="INSIDE info views by category" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : inside_views.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={192}>
                    {/* bottom:60 ensures rotated category labels aren't clipped */}
                    <BarChart data={inside_views} margin={{ left: 0, right: 8, top: 4, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="category"
                        tick={{ fontSize: 11 }}
                        angle={-35}
                        textAnchor="end"
                        interval={0}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill={CHART_COLOR} radius={[4, 4, 0, 0]} name="Views" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Content by source (regional vs property-private POIs) */}
            <Card>
              <SectionHeader title="Content by source" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-24 w-full" />
                ) : content_source_breakdown.length === 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Regional / hotel breakdown available after new guest sessions.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Events recorded before source tracking was added are not included.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const regional = content_source_breakdown.find((r) => r.source === "public")
                      const hotel    = content_source_breakdown.find((r) => r.source === "private")
                      return (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Regional</span>
                            <span className="tabular-nums font-medium">
                              {(regional?.count ?? 0).toLocaleString()} views
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Hotel</span>
                            <span className="tabular-nums font-medium">
                              {(hotel?.count ?? 0).toLocaleString()} views
                            </span>
                          </div>
                          <p className="pt-1 text-xs text-muted-foreground">
                            Events before source tracking was added are not included.
                          </p>
                        </>
                      )
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Peak hours */}
            <Card className="lg:col-span-2">
              <SectionHeader title="Activity by day & hour" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-40 w-full" />
                ) : (
                  <PeakHoursTable data={peak_hours} />
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  )
}

AnalyticsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
